import React, { useCallback, useMemo, useState } from "react";
import { AgGridTable, LoadingState } from "components";
import { useReport, useWmsItem } from "hooks";
import { S2dQuery, S2dRecord } from "types";
import SelectMulti from "components/ui-common/SelectMulti";

const today = new Date();
const formatDate = (d: Date) => d.toISOString().slice(0, 10);
const startOfYear = `${today.getFullYear()}-01-01`;
const todayString = formatDate(today);

const defaultFilters: S2dQuery = {
  from_date: startOfYear,
  to_date: todayString,
  product_ids: [],
};

const S2dReport: React.FC = () => {
  const { fetchS2dHkd } = useReport();
  const { fetchList: fetchProducts } = useWmsItem();
  const { data: products = [] } = fetchProducts(true);

  const productOptions = useMemo(
    () =>
      (products as any[])?.map?.((p: any) => ({
        value: String(p.id),
        label: `${p.code} - ${p.name}`,
      })) ?? [],
    [products],
  );

  const [filters, setFilters] = useState<S2dQuery>(defaultFilters);
  const [submitted, setSubmitted] = useState<S2dQuery | null>(null);

  const handleFilterChange = useCallback((key: keyof S2dQuery, value: any) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleSubmit = useCallback(
    (event?: React.FormEvent) => {
      event?.preventDefault();
      if (!filters.from_date || !filters.to_date) {
        return;
      }
      setSubmitted(filters);
      fetchS2dHkd.mutate(filters);
    },
    [fetchS2dHkd, filters],
  );

  const records = (fetchS2dHkd.data?.data ?? []) as S2dRecord[];

  const totals = useMemo(
    () =>
      records.reduce(
        (acc, item) => {
          acc.opening_amount += Number(item.opening_amount ?? 0) || 0;
          acc.import_amount += Number(item.import_amount ?? 0) || 0;
          acc.export_amount += Number(item.export_amount ?? 0) || 0;
          acc.closing_amount += Number(item.closing_amount ?? 0) || 0;
          return acc;
        },
        {
          opening_amount: 0,
          import_amount: 0,
          export_amount: 0,
          closing_amount: 0,
        },
      ),
    [records],
  );

  const columns = useMemo(
    () => [
      {
        headerName: "Mã hàng",
        field: "product_code",
        minWidth: 120,
      },
      {
        headerName: "Tên hàng",
        field: "product_name",
        minWidth: 200,
        flex: 1,
      },
      {
        headerName: "ĐVT",
        field: "unit",
        minWidth: 80,
      },
      {
        headerName: "Tồn đầu (SL)",
        field: "opening_qty",
        minWidth: 120,
        valueFormatter: (params: any) =>
          params.value !== undefined && params.value !== null
            ? Number(params.value).toLocaleString("vi-VN")
            : "0",
        cellClass: "text-right",
      },
      {
        headerName: "Tồn đầu (GT)",
        field: "opening_amount",
        minWidth: 140,
        valueFormatter: (params: any) =>
          params.value !== undefined && params.value !== null
            ? Number(params.value).toLocaleString("vi-VN") + " ₫"
            : "0 ₫",
        cellClass: "text-right font-semibold",
      },
      {
        headerName: "Nhập (SL)",
        field: "import_qty",
        minWidth: 120,
        valueFormatter: (params: any) =>
          params.value !== undefined && params.value !== null
            ? Number(params.value).toLocaleString("vi-VN")
            : "0",
        cellClass: "text-right text-green-700",
      },
      {
        headerName: "Nhập (GT)",
        field: "import_amount",
        minWidth: 140,
        valueFormatter: (params: any) =>
          params.value !== undefined && params.value !== null
            ? Number(params.value).toLocaleString("vi-VN") + " ₫"
            : "0 ₫",
        cellClass: "text-right font-semibold text-green-700",
      },
      {
        headerName: "Xuất (SL)",
        field: "export_qty",
        minWidth: 120,
        valueFormatter: (params: any) =>
          params.value !== undefined && params.value !== null
            ? Number(params.value).toLocaleString("vi-VN")
            : "0",
        cellClass: "text-right text-red-700",
      },
      {
        headerName: "Xuất (GT)",
        field: "export_amount",
        minWidth: 140,
        valueFormatter: (params: any) =>
          params.value !== undefined && params.value !== null
            ? Number(params.value).toLocaleString("vi-VN") + " ₫"
            : "0 ₫",
        cellClass: "text-right font-semibold text-red-700",
      },
      {
        headerName: "Tồn cuối (SL)",
        field: "closing_qty",
        minWidth: 120,
        valueFormatter: (params: any) =>
          params.value !== undefined && params.value !== null
            ? Number(params.value).toLocaleString("vi-VN")
            : "0",
        cellClass: "text-right font-bold",
      },
      {
        headerName: "Tồn cuối (GT)",
        field: "closing_amount",
        minWidth: 140,
        valueFormatter: (params: any) =>
          params.value !== undefined && params.value !== null
            ? Number(params.value).toLocaleString("vi-VN") + " ₫"
            : "0 ₫",
        cellClass: "text-right font-bold text-blue-700",
      },
    ],
    [],
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      resizable: true,
      minWidth: 100,
    }),
    [],
  );

  const isLoading = fetchS2dHkd.isPending;

  return (
    <div className="p-6 space-y-6">
      {isLoading && (
        <LoadingState
          message="Đang tải báo cáo"
          description="Vui lòng chờ trong giây lát."
        />
      )}

      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500 uppercase">Báo cáo S2d-HKD</p>
          <h1 className="text-2xl font-semibold text-gray-900">
            Sổ chi tiết vật liệu, công cụ, dụng cụ, hàng hóa
          </h1>
          <p className="text-sm text-gray-600">
            Theo dõi nhập - xuất - tồn kho theo thời gian.
          </p>
        </div>
        <button
          type="button"
          onClick={handleSubmit}
          className="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-blue-700"
          disabled={isLoading}
        >
          {isLoading ? "Đang tải..." : "Xem báo cáo"}
        </button>
      </div>

      <form
        onSubmit={handleSubmit}
        className="rounded-lg border border-gray-200 bg-white shadow-sm p-4 space-y-4"
      >
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Từ ngày * (ngày hạch toán)
            </label>
            <input
              type="date"
              value={filters.from_date}
              onChange={(e) => handleFilterChange("from_date", e.target.value)}
              className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
              required
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Đến ngày * (ngày hạch toán)
            </label>
            <input
              type="date"
              value={filters.to_date}
              onChange={(e) => handleFilterChange("to_date", e.target.value)}
              className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
              required
            />
          </div>
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">
            Lọc theo hàng hóa (tùy chọn)
          </label>
          <SelectMulti
            isMulti
            isClearable
            options={productOptions}
            value={productOptions.filter((opt) =>
              (filters.product_ids ?? []).map(String).includes(opt.value),
            )}
            placeholder="Chọn hàng hóa - Để trống để xem tất cả"
            onChange={(vals: any) =>
              handleFilterChange(
                "product_ids",
                Array.isArray(vals) ? vals.map((v: any) => v.value) : [],
              )
            }
            className="react-select-container"
            classNamePrefix="react-select"
          />
          <p className="text-xs text-gray-500">
            💡 Theo dõi nhập - xuất - tồn kho theo từng sản phẩm. Có thể chọn
            nhiều sản phẩm.
          </p>
        </div>
        <div className="flex items-center justify-end">
          <button
            type="submit"
            className="rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            disabled={isLoading}
          >
            Áp dụng bộ lọc
          </button>
        </div>
      </form>

      {submitted && (
        <div className="rounded-lg border border-gray-200 bg-white shadow-sm p-4 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold text-gray-900">
              Kết quả ( {submitted.from_date} → {submitted.to_date} )
            </h2>
            <div className="flex flex-wrap items-center gap-6 text-right">
              <div>
                <p className="text-xs text-gray-500">Tồn đầu</p>
                <p className="text-lg font-semibold text-gray-900">
                  {totals.opening_amount.toLocaleString("vi-VN")} ₫
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Nhập</p>
                <p className="text-lg font-semibold text-green-700">
                  {totals.import_amount.toLocaleString("vi-VN")} ₫
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Xuất</p>
                <p className="text-lg font-semibold text-red-700">
                  {totals.export_amount.toLocaleString("vi-VN")} ₫
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Tồn cuối</p>
                <p className="text-lg font-semibold text-blue-700">
                  {totals.closing_amount.toLocaleString("vi-VN")} ₫
                </p>
              </div>
            </div>
          </div>
          <AgGridTable
            rows={records}
            columns={columns as any}
            defaultColDef={defaultColDef}
            pagination={false}
            autoHeight
          />
        </div>
      )}
    </div>
  );
};

export default S2dReport;
